import * as React from 'react'
import { useCallback, useEffect, useState } from 'react'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import { Id, toast } from 'react-toastify'
import { ENTITY_TYPES, post } from '@sales-center/common'
import {
  getOpportunities,
  getOpportunityDetail,
  SplitOpportunityInput,
} from '@user/store/opportunity'
import { getLeadDetail, getLeadList } from '@user/store/lead'
import { useNavigate } from 'react-router-dom'
import Link from '@mui/material/Link'
import { ServiceConfig } from '@user/store/services/config'
import Typography from '@mui/material/Typography'
import { removeLeadOppProducts } from '@user/store/leadOppProducts'

export type Props = {
  saveForLaterToastIdRef: React.MutableRefObject<Id | null>
}

export function useOpportunitySplit({ saveForLaterToastIdRef }: Props) {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const [status, setStatus] = useState<'idle' | 'pending' | 'success' | 'fail'>(
    'idle',
  )

  const [toSplit, setToSplit] = useState<SplitOpportunityInput>()

  const execute = useCallback(
    (input: SplitOpportunityInput) => {
      if (status != 'pending') {
        setToSplit(input)
      }
    },
    [status],
  )

  useEffect(() => {
    let mounted = true

    const toastMessage = (id) => (
      <Typography>
        New Opportunity created.
        <br />
        <Link
          onClick={() => navigate(`/opportunities/${id}`)}
          sx={{ cursor: 'pointer' }}
        >
          View Opportunity
        </Link>
      </Typography>
    )

    const execute = async () => {
      if (toSplit) {
        try {
          setStatus('pending')

          const url = `${ServiceConfig.baseAPIUrl}/Opportunity${ServiceConfig.opportunity.split}`

          const response = await post(`${url}`, toSplit)

          if (response.ok) {
            dispatch(removeLeadOppProducts(toSplit.originProductIds))

            const newId = await response.json()

            if (toSplit.originEntityType === ENTITY_TYPES.Lead) {
              dispatch(getLeadDetail(toSplit.originEntityId))
              dispatch(getLeadList())
            } else {
              dispatch(getOpportunityDetail(toSplit.originEntityId))
            }
            dispatch(getOpportunities())

            setStatus('success')
            saveForLaterToastIdRef.current = toast.success(
              toastMessage(newId),
              {
                autoClose: 10000,
                position: 'bottom-right',
                style: {
                  top: -70,
                },
              },
            )
          } else {
            setStatus('fail')
            toast.error(
              'Error creating new opportunity. Please try again or contact support.',
            )
          }
        } catch (error) {
          if (mounted) {
            setStatus('fail')
            toast.error(error.Message)
          }
        } finally {
          setToSplit(undefined)
          setStatus('idle')
        }
      }
    }

    execute()

    return () => {
      mounted = false
    }
  }, [dispatch, navigate, toSplit])

  return { status, execute }
}
